import type { ErrorInfo } from "react";
import { hydrateRoot } from "react-dom/client";
import type { FormName } from "@searchui-searchforms/shared/config";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { invariant } from "@searchui-searchforms/shared/utils";
import { HydrationEntry } from "@/app/lib/render-entries";
import { createI18nInstance } from "@/shared/i18n";
import { EN_LANGUAGE } from "@/shared/i18n/en-language";
import { logFCI } from "@/shared/lib/fci";
import { makeQueryClient } from "@/shared/queryClient";
import type { ClassicFormProps } from "@/pages/classic-form";
import type { SliderFormProps } from "@/pages/slider-form";
import { SFS_RENDER_ROOT } from "./constants";

type ComponentProps = ClassicFormProps | SliderFormProps;

type AttachArguments =
    | {
          id: string;
          parent?: undefined;
      }
    | {
          id?: undefined;
          parent: HTMLElement;
      };

export const registerAttach = (Component: React.ComponentType<ComponentProps>, formName: FormName) => {
    const cleanup = new Map<HTMLElement, () => void>();

    const getRoot = ({ id, parent }: AttachArguments): HTMLElement => {
        if (id) {
            const node = document.getElementById(id);
            invariant(node, "[webpart] No root element found for id");
            return node;
        }
        const child = parent?.querySelector(`[${SFS_RENDER_ROOT}]`);
        invariant(child, `[webpart] No render root found inside parent`);
        return child as HTMLElement;
    };

    const unmount = (params: AttachArguments) => {
        const rootEl = getRoot(params);
        const cleanupFunc = cleanup.get(rootEl);
        if (cleanupFunc) {
            cleanupFunc();
            cleanup.delete(rootEl);
        }
    };
    const attach = async (params: AttachArguments) => {
        try {
            const rootEl = getRoot(params);
            const props = rootEl.dataset.ssrProps;
            invariant(typeof props === "string", "[webpart] No props found at root element");

            const parsedProps = JSON.parse(props);

            const language = rootEl.dataset.i18nLang || EN_LANGUAGE;
            const i18nInstance = await createI18nInstance({
                language,
            });

            const queryClient = makeQueryClient();

            const handleRecoverableError = (error: unknown, errorInfo: ErrorInfo) => {
                logFCI({
                    errorType: FCI_ERROR.TransparentSubstitution,
                    errorInfo: `Recoverable webpart render error: ${error}`,
                    details: {
                        stack: error instanceof Error ? error.stack : undefined,
                        componentStack: errorInfo.componentStack ?? undefined,
                        formName,
                    },
                });
            };

            const reactDOMRoot = hydrateRoot(
                rootEl,
                <HydrationEntry
                    Component={Component}
                    componentProps={{ ...parsedProps, i18n: i18nInstance, queryClient }}
                />,
                {
                    onRecoverableError: handleRecoverableError,
                },
            );
            cleanup.set(rootEl, () => reactDOMRoot.unmount());
        } catch (error) {
            logFCI({
                errorType: FCI_ERROR.Main,
                errorInfo: `webpart attach() failed: ${error}`,
                details: {
                    stack: error instanceof Error ? error.stack : undefined,
                    formName,
                },
            });
            throw error;
        }
    };

    const webpartApi = window.ancestry?.search?.forms?.api?.webpart;
    if (webpartApi) {
        const attachFn = webpartApi[formName]?.attach;
        const queue = attachFn?.queue;
        if (queue) {
            // Prevent mounting duplicate IDs
            const attachedSet = new Set();
            queue.forEach((entry) => {
                // Either id or a parent node
                const key = entry.id ?? entry.parent;
                if (!attachedSet.has(key)) {
                    attach(entry);
                    attachedSet.add(key);
                }
            });
            attachFn.queue = [];
        }
    }

    if (!window.ancestry) window.ancestry = {};
    if (!window.ancestry.search) window.ancestry.search = {};
    if (!window.ancestry.search.forms) window.ancestry.search.forms = {};
    if (!window.ancestry.search.forms.api) window.ancestry.search.forms.api = {};
    if (!window.ancestry.search.forms.api.webpart) window.ancestry.search.forms.api.webpart = {};
    window.ancestry.search.forms.api.webpart[formName] = { attach, unmount };
};
